import React from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import '../../styles/CarrierDownload.css';

export interface CallHistoryItem {
  id: number;
  parentCallSid: string;
  callSid?: string;
  callStatus: string;
  duration: string;
  fromNumber: string;
  toNumber: string;
  startedOn: string;
  endedOn?: string;
  recordingSid?: string;
  recordingUrl?: string;
  transcriptText?: string;
  summary?: string;
  callNotes?: string;
  conferenceSid?: string;
  insuredId?: string;
  insuredName?: string;
}

const ITEMS_OPTIONS = [10, 20, 50];

function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

// The backend stores/serializes StartedOn as SQL Server DATETIME (always UTC, set via
// GETUTCDATE()), but that type carries no timezone info — so the JSON string comes
// through with no "Z"/offset suffix, and `new Date(...)` would otherwise parse it as
// local time instead of UTC. Appending "Z" when it's missing forces the correct
// UTC-to-local conversion.
export function parseUtcDate(iso?: string): Date | null {
  if (!iso) return null;
  const hasTz = /[Zz]|[+-]\d\d:\d\d$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(iso?: string): string {
  const d = parseUtcDate(iso);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

export function formatDurationSeconds(value?: string): string {
  const secs = Number(value);
  if (!value || isNaN(secs)) return '—';
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return `${mins.toString().padStart(2, '0')}:${rem.toString().padStart(2, '0')}`;
}

export function statusMeta(callStatus?: string): { label: string; tone: 'consumed' | 'partial' | 'not-consumed' } {
  const s = (callStatus || '').toLowerCase();
  if (s === 'completed') return { label: 'Completed', tone: 'consumed' };
  if (['failed', 'busy', 'no-answer', 'canceled'].includes(s)) return { label: 'Failed', tone: 'not-consumed' };
  if (['in-progress', 'ringing', 'queued'].includes(s)) return { label: 'In Progress', tone: 'partial' };
  return { label: 'Unknown', tone: 'partial' };
}

interface DialerHistoryProps {
  items: CallHistoryItem[];
  loading: boolean;
  error: boolean;
  page: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  onItemsPerPageChange: (n: number) => void;
  total: number;
  onSelectItem?: (item: CallHistoryItem) => void;
  selectedId?: number | null;
}

const DialerHistory: React.FC<DialerHistoryProps> = ({
  items, loading, error, page, onPageChange, itemsPerPage, onItemsPerPageChange, total,
  onSelectItem, selectedId,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const startItem = total === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const endItem = Math.min(page * itemsPerPage, total);

  return (
    <div className="dialer-history-wrap">
    <div className="cdsl-card cdsl-card--table">
      {loading ? (
        <div className="cdsl-skeleton">
          {Array.from({ length: itemsPerPage }).map((_, i) => <div key={i} className="cdsl-skeleton-row" />)}
        </div>
      ) : error ? (
        <div className="cdsl-empty">Failed to load call history.</div>
      ) : items.length === 0 ? (
        <div className="cdsl-empty">No calls yet.</div>
      ) : (
        <div className="cdsl-table-wrap">
          <table className="cdsl-table">
            <thead>
              <tr>
                <th className="cdsl-th">Status</th>
                <th className="cdsl-th">Number</th>
                <th className="cdsl-th">Started At</th>
                <th className="cdsl-th">Duration</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const meta = statusMeta(item.callStatus);
                return (
                  <tr
                    key={item.id}
                    className={`cdsl-tr${onSelectItem ? ' cdsl-tr--clickable' : ''}${selectedId === item.id ? ' cdsl-tr--active' : ''}`}
                    onClick={() => onSelectItem?.(item)}
                  >
                    <td className="cdsl-td">
                      <span className={`cdsl-status cdsl-status--${meta.tone}`}>{meta.label}</span>
                    </td>
                    <td className="cdsl-td">+{item.toNumber || '—'}</td>
                    <td className="cdsl-td cdsl-td--date">{formatDateTime(item.startedOn)}</td>
                    <td className="cdsl-td">{formatDurationSeconds(item.duration)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && (
        <div className="cdsl-pagination">
          <div className="cdsl-pagination-pages">
            <button className="cdsl-pg-btn" disabled={page === 1} onClick={() => onPageChange(1)} title="First">⟨⟨</button>
            <button className="cdsl-pg-btn" disabled={page === 1} onClick={() => onPageChange(page - 1)} title="Previous">
              <FiChevronLeft size={13} />
            </button>

            {buildPageNumbers(page, totalPages).map((p, i) =>
              p === '...'
                ? <span key={`dots-${i}`} className="cdsl-pg-dots">...</span>
                : <button
                    key={p}
                    className={`cdsl-pg-btn${page === p ? ' cdsl-pg-btn--active' : ''}`}
                    onClick={() => onPageChange(p)}
                  >{p}</button>
            )}

            <button className="cdsl-pg-btn" disabled={page === totalPages} onClick={() => onPageChange(page + 1)} title="Next">
              <FiChevronRight size={13} />
            </button>
            <button className="cdsl-pg-btn" disabled={page === totalPages} onClick={() => onPageChange(totalPages)} title="Last">⟩⟩</button>
          </div>

          <div className="cdsl-pagination-meta">
            <select
              className="cdsl-per-page-select"
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            >
              {ITEMS_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="cdsl-pagination-info">items per page</span>
            <span className="cdsl-pagination-count">{startItem} - {endItem} of {total} items</span>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default DialerHistory;
